import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { Alert, Button, Typography } from '@mui/material';
import { DataGrid, gridNumberComparator } from '@mui/x-data-grid';
import { BackdropLoader, Icon, palette, SimpleModal, toast } from '@livable/saas-react-components';
import { reaction } from 'mobx';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { useOldServicePeriodConfirm } from 'src/components/allocations/OldServicePeriod';
import { track, events } from 'src/configs/amplitude';
import { ACKNOWLEDGEMENT_REQUIRED, DRAFT_CHANGED } from 'src/constants/submeterBilling';
import { useStore } from 'src/stores';
import useMeterDetailsStyles from './styles';
import noDataSvg from 'src/img/no-data.svg';
import { toSaveRows, toUpdateRows, toRecalculateRequest, findDivergentMeter, toInitialMeterRate, toOverrideRate, multiplyToCents, toMeasure, resolveRateEdit, formatMeterRate, formatMoney, findUnitsMissingReads, toUsageFromReads, formatUsage, haveReadsChanged } from './toSaveRows';
import { VarianceReview } from './VarianceReview';
import { OldUtilityConfigurationModal } from 'src/pages/NewBillingModule/OldUtilityConfigurationModal';
import { getColumnMapVersion } from 'src/pages/NewBillingModule/OldUtilityConfigurationModal/columnMapVersion';
import { ParseWarnings } from './ParseWarnings';
import { toSplitRowShares } from './splitRowDisplay';
import { toBalancedMeterKeys, toMeterTotalMismatches } from './sharedMeterTotals';

// A row with one of these codes is dropped from the parse, so each warning with one counts as a skipped row.
const UNMATCHED_PARSE_CODES = ['unrecognized_meter_id', 'unit_name_not_mapped', 'meter_id_not_mapped', 'meter_id_on_several_units', 'unit_meter_conflict'];
const SKIPPED_PARSE_CODES = [...UNMATCHED_PARSE_CODES, 'invalid_value'];
const NO_BILL_NOTICE = 'This cycle has no bill. Add it on Bill details.';
const NO_BILL_EMPTY_STATE = 'Meter details appear once the cycle has a bill.';
const NO_BILL_SUBTITLE = 'Meter reads can be entered once the cycle has a bill.';

// Halves round away from zero, matching the backend's MidpointRounding.AwayFromZero. Math.round alone
// rounds a half toward positive infinity, which disagrees on a negative amount: -1.5 becomes -1, not -2.
// `|| 0` turns the -0 a small negative rounds to back into 0, which the grid would otherwise print as "-0".
const roundHalfAway = v => Math.sign(v) * Math.round(Math.abs(v)) || 0;
const round2 = v => Math.sign(v) * Math.round((Math.abs(v) + Number.EPSILON) * 100) / 100 || 0;
const num = v => Number(v) || 0;

// Module level, so a rebuild still counts as running after its screen unmounts and mounts again.
const rebuildsInFlight = new Set();
// Row writes (Save's PUT, the upload save) per cycle, so a screen opened during one waits for it before reading rows.
const rowWritesInFlight = new Map();
// Approvals per cycle from the start of their save to the end of their approve request, which row writes alone do not span.
const approvalsInFlight = new Set();
const cycleWorkListeners = new Set();
const notifyCycleWork = () => cycleWorkListeners.forEach(listener => listener());
const setRebuildInFlight = (key, isRunning) => {
  if (isRunning) rebuildsInFlight.add(key);else rebuildsInFlight.delete(key);
  notifyCycleWork();
};
const trackRowWrite = async (key, write) => {
  var _rowWritesInFlight$ge;
  rowWritesInFlight.set(key, ((_rowWritesInFlight$ge = rowWritesInFlight.get(key)) !== null && _rowWritesInFlight$ge !== void 0 ? _rowWritesInFlight$ge : 0) + 1);
  notifyCycleWork();
  try {
    const result = await write();
    return result;
  } finally {
    const count = rowWritesInFlight.get(key) - 1;
    if (count > 0) rowWritesInFlight.set(key, count);else rowWritesInFlight.delete(key);
    notifyCycleWork();
  }
};
const trackApproval = (key, approve) => {
  approvalsInFlight.add(key);
  return approve().finally(() => approvalsInFlight.delete(key));
};
const subscribeToCycleWork = listener => {
  cycleWorkListeners.add(listener);
  return () => cycleWorkListeners.delete(listener);
};
const toastFailure = asyncStore => {
  var _error$errorResp;
  const error = asyncStore.error;
  toast('error', (_error$errorResp = error === null || error === void 0 ? void 0 : error.errorResp) !== null && _error$errorResp !== void 0 ? _error$errorResp : {
    title: 'Error',
    body: [(error === null || error === void 0 ? void 0 : error.message) || 'Error']
  });
};
const USAGE_SCALE = 10000;
const toUsageUnits = v => Math.round(num(v) * USAGE_SCALE);
const formatUsageUnits = units => (units / USAGE_SCALE).toLocaleString('en-US', {
  maximumFractionDigits: 4
});
export const sumMeteredUsage = (rows, countEachMeterOnce) => {
  const seen = new Set();
  return rows.reduce((sum, row) => {
    if (countEachMeterOnce) {
      var _row$meterSerialNumbe;
      const key = "".concat(row.unitId, "::").concat((_row$meterSerialNumbe = row.meterSerialNumber) !== null && _row$meterSerialNumbe !== void 0 ? _row$meterSerialNumbe : '');
      if (seen.has(key)) return sum;
      seen.add(key);
    }
    return sum + toUsageUnits(row.meterUsage);
  }, 0);
};

/*
 * An exact copy of the backend share splitter's arithmetic, which runs in .NET decimal (a 96-bit
 * integer mantissa and a scale of at most 28), reproduced with BigInt because a float product can
 * land exactly on a half cent where the decimal one falls just short of it. Each step rounds where
 * decimal does: the day share to 28 decimal places, the total times the share to whatever still fits
 * in 96 bits (both half to even), then the amount to the cent, half away from zero.
 */
const DECIMAL_MAX_SCALE = 28;

// Days arrive as whatever the grid holds. The backend stores them as whole days, and BigInt throws on
// a fraction, so anything else counts as its whole part and a negative or non-number counts as none.
const toWholeDays = d => Number.isFinite(d) && d > 0 ? Math.floor(d) : 0;
export const splitCentsByDays = (totalCents, rawDays) => {
  const days = rawDays.map(toWholeDays);
  const totalDays = days.reduce((sum, d) => sum + d, 0);
  if (totalDays <= 0) return days.map(() => 0);
  const safeTotalCents = Number.isFinite(totalCents) ? totalCents : 0;
  if (days.length === 1) return [safeTotalCents];

  // Built here rather than at module scope, so importing this file never needs BigInt: a browser
  // without it fails this split, not the whole app.
  const zero = BigInt(0);
  const one = BigInt(1);
  const two = BigInt(2);
  const mantissaLimit = two ** BigInt(96);
  const pow10 = n => BigInt(10) ** BigInt(n);
  const bigAbs = n => n < zero ? -n : n;
  const divideRounded = (n, d, halfToEven) => {
    let q = n / d;
    const twiceRemainder = two * bigAbs(n % d);
    if (twiceRemainder > d || twiceRemainder === d && (!halfToEven || q % two !== zero)) {
      q += n < zero ? -one : one;
    }
    return q;
  };
  const total = BigInt(safeTotalCents);
  const cents = days.map(d => {
    const share = divideRounded(BigInt(d) * pow10(DECIMAL_MAX_SCALE), BigInt(totalDays), true);
    const product = total * share;
    const productScale = 2 + DECIMAL_MAX_SCALE;
    let drop = productScale - DECIMAL_MAX_SCALE;
    while (bigAbs(divideRounded(product, pow10(drop), true)) >= mantissaLimit) drop += 1;
    const amount = divideRounded(product, pow10(drop), true);
    return divideRounded(amount, pow10(productScale - drop - 2), false);
  });
  const amounts = cents.map(Number);
  const remainder = safeTotalCents - amounts.reduce((sum, c) => sum + c, 0);
  const step = remainder < 0 ? -1 : 1;
  days.map((_, idx) => idx).sort((a, b) => days[b] - days[a]).filter(idx => Math.sign(amounts[idx] + step) !== -Math.sign(safeTotalCents)).slice(0, Math.abs(remainder)).forEach(idx => {
    amounts[idx] += step;
  });
  return amounts;
};

/**
 * A saved row's meterRate, usageCharge and allocationCharge are three independent fields on the
 * wire. Editing one directly must not leave the other two stale, but a directly typed usageCharge
 * or allocationCharge must also survive — so this derives only what the diff between newRow and
 * oldRow implies changed, rather than recomputing every field on every save. Single-residency
 * only: a meter shared by more than one residency goes through deriveMeterGroupCharges below.
 */
export const deriveCharges = (newRow, oldRow) => {
  const inputChanged = ['meterRate', 'meterUsage', 'baseCharge'].some(k => num(newRow[k]) !== num(oldRow[k]));
  if (inputChanged) {
    const usageCharge = multiplyToCents(toMeasure(num(newRow.meterUsage)), num(newRow.meterRate));
    return _objectSpread(_objectSpread({}, newRow), {}, {
      usageCharge,
      allocationCharge: round2(num(newRow.baseCharge) + usageCharge)
    });
  }
  if (num(newRow.usageCharge) !== num(oldRow.usageCharge)) {
    return _objectSpread(_objectSpread({}, newRow), {}, {
      allocationCharge: round2(num(newRow.baseCharge) + num(newRow.usageCharge))
    });
  }
  return newRow;
};
const idKey = v => String(v !== null && v !== void 0 ? v : '').toLowerCase();
const compareIds = (a, b) => {
  if (a === b) return 0;
  return a < b ? -1 : 1;
};
const byResidencyThenRow = (a, b) => compareIds(idKey(a.residencyId), idKey(b.residencyId)) || compareIds(idKey(a.allocationId), idKey(b.allocationId));

/**
 * A saved row is per residency, but a meter shared by more than one residency (a mid-cycle
 * move-out/move-in on the same unit) stores baseCharge and usageCharge — the meter's own total —
 * duplicated on every one of those rows; only allocationCharge differs, split by
 * serviceDaysInPeriod. deriveCharges alone sets allocationCharge = base + usage on just the edited
 * row, which bills the whole meter to that one resident, leaves siblings on their old values, and
 * leaves the group with mismatched rates the backend refuses on the next edit. On a rate, usage or
 * base edit, or a directly typed usage charge, this applies the edit and the recomputed total to
 * every row sharing the same meter (groupRows), and splits the total across them by
 * serviceDaysInPeriod. The split is the backend's own share splitter (splitCentsByDays above), which
 * the save path and the bill re-price path both use: each share rounds to the cent, the
 * remainder goes a cent at a time to the longest stays, and a group with no service days gets zero
 * on every row. A change to this split has to land on both sides
 * together. The backend re-price still splits its own total, built from the bill's full-precision
 * rate and base charge rather than the rounded values on these rows, so it can move a cent. A
 * single-row group, or a directly typed total charge (which deriveCharges already keeps row-local),
 * reduces to deriveCharges' own single-row result. An edit to either read also copies both the
 * start and end read to every row of the meter, because the backend refuses the next bill edit
 * when the rows of one meter disagree on them.
 */
export const deriveMeterGroupCharges = (newRow, oldRow, groupRows) => {
  var _derived$overrideRate, _oldRow$overrideRate;
  const derived = deriveCharges(newRow, oldRow);
  const inputChanged = ['meterRate', 'meterUsage', 'baseCharge'].some(k => num(newRow[k]) !== num(oldRow[k]));
  const usageChargeTyped = !inputChanged && num(newRow.usageCharge) !== num(oldRow.usageCharge);
  const overrideRate = (_derived$overrideRate = derived.overrideRate) !== null && _derived$overrideRate !== void 0 ? _derived$overrideRate : null;
  const overrideChanged = overrideRate !== ((_oldRow$overrideRate = oldRow.overrideRate) !== null && _oldRow$overrideRate !== void 0 ? _oldRow$overrideRate : null);
  const readsChanged = haveReadsChanged(newRow, oldRow);
  const reads = {
    startMeterRead: derived.startMeterRead,
    endMeterRead: derived.endMeterRead
  };
  const meterGroup = groupRows && groupRows.length > 1 ? groupRows : [newRow];
  if (meterGroup.length <= 1) return [derived];
  const meterRows = meterGroup.map(r => r.id === newRow.id ? derived : r);
  if (!inputChanged && !usageChargeTyped) {
    if (!overrideChanged && !readsChanged) return [derived];
    return meterRows.map(r => _objectSpread(_objectSpread(_objectSpread({}, r), overrideChanged && {
      overrideRate
    }), readsChanged && reads));
  }
  const total = round2(num(derived.baseCharge) + num(derived.usageCharge));
  const ordered = meterRows.map((row, idx) => ({
    row,
    idx
  })).sort((a, b) => byResidencyThenRow(a.row, b.row));
  const cents = splitCentsByDays(roundHalfAway(total * 100), ordered.map(_ref => {
    let row = _ref.row;
    return num(row.serviceDaysInPeriod);
  }));
  const shares = [];
  ordered.forEach((_ref2, pos) => {
    let idx = _ref2.idx;
    shares[idx] = cents[pos] / 100;
  });
  return meterRows.map((r, idx) => _objectSpread(_objectSpread({}, r), {}, {
    meterRate: derived.meterRate,
    meterUsage: derived.meterUsage,
    baseCharge: derived.baseCharge,
    usageCharge: derived.usageCharge,
    allocationCharge: shares[idx],
    overrideRate
  }, readsChanged && reads));
};
export const MeterDetails = observer(props => {
  var _store$account, _utility$bill, _utility$bill2, _utility$bill3;
  const propertyId = props.propertyId,
    utilityId = props.utilityId,
    billingCycleId = props.billingCycleId,
    billUsage = props.billUsage,
    utility = props.utility,
    onRowsSaved = props.onRowsSaved,
    onNoRows = props.onNoRows,
    onBusy = props.onBusy,
    onApproved = props.onApproved,
    servicePeriod = props.servicePeriod;
  const classes = useMeterDetailsStyles();
  const _useTranslation = useTranslation(),
    t = _useTranslation.t;
  const store = useStore();
  const oldServicePeriod = useOldServicePeriodConfirm();
  // The bill-removed effect below closes its dialog; a ref keeps that effect keyed on the bill alone.
  const closeOldServicePeriodRef = useRef(oldServicePeriod.close);
  closeOldServicePeriodRef.current = oldServicePeriod.close;
  const fileInputRef = useRef(null);
  const page = 'Apply Billing';
  const propertyUtilityData = store.properties.propertyUtilities.find(u => u.utilityId === utilityId);
  const propertyUtilityId = propertyUtilityData === null || propertyUtilityData === void 0 ? void 0 : propertyUtilityData.propertyUtilityId;

  // Store actions cancel each other's in-flight calls, so a closed screen must not start new reads.
  const isActiveRef = useRef(true);
  useEffect(() => () => {
    isActiveRef.current = false;
  }, []);
  const getSelectedPropertyId = () => {
    var _store$homeDashboardS;
    return (_store$homeDashboardS = store.homeDashboardStore.getCurrentProperty) === null || _store$homeDashboardS === void 0 ? void 0 : _store$homeDashboardS.id;
  };
  const selectedPropertyId = getSelectedPropertyId();
  const selectionRef = useRef({
    id: selectedPropertyId,
    generation: 0
  });
  const advanceSelection = id => {
    if (selectionRef.current.id !== id) {
      selectionRef.current = {
        id,
        generation: selectionRef.current.generation + 1
      };
    }
  };
  advanceSelection(selectedPropertyId);
  const _useState = useState(null),
    _useState2 = _slicedToArray(_useState, 2),
    mappingOpenFor = _useState2[0],
    setMappingOpenFor = _useState2[1];
  useEffect(() => reaction(getSelectedPropertyId, id => {
    advanceSelection(id);
    setMappingOpenFor(null);
  }), [] // eslint-disable-line react-hooks/exhaustive-deps
  );
  const _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    isOpeningMapping = _useState4[0],
    setIsOpeningMapping = _useState4[1];
  const isOpeningMappingRef = useRef(false);
  const uploadSeqRef = useRef(0);
  const columnMapReadsRef = useRef(0);
  const mappingOpenedDetailsRef = useRef(null);
  useEffect(() => {
    setMappingOpenFor(null);
  }, [selectedPropertyId]);
  const getSelectedPropertyDetails = () => {
    var _store$homeDashboardS2, _store$homeDashboardS3;
    return (_store$homeDashboardS2 = store.homeDashboardStore.getCurrentCompany) === null || _store$homeDashboardS2 === void 0 || (_store$homeDashboardS3 = _store$homeDashboardS2.selectedPropertyDetails) === null || _store$homeDashboardS3 === void 0 || (_store$homeDashboardS3 = _store$homeDashboardS3.call(_store$homeDashboardS2)) === null || _store$homeDashboardS3 === void 0 ? void 0 : _store$homeDashboardS3.details;
  };
  const currentPropertyDetails = getSelectedPropertyDetails();
  const canFixMapping = !((_store$account = store.account) !== null && _store$account !== void 0 && (_store$account = _store$account.featureFlagsStore) !== null && _store$account !== void 0 && _store$account.getIsBillingTemplatesEnabled) && selectedPropertyId === propertyId && !!currentPropertyDetails;
  const handleFixMapping = async () => {
    if (isOpeningMappingRef.current) return;
    isOpeningMappingRef.current = true;
    setIsOpeningMapping(true);
    const openedFor = getSelectedPropertyId();
    const details = currentPropertyDetails;
    const generation = selectionRef.current.generation;
    const isSameScreen = () => isActiveRef.current && selectionRef.current.generation === generation && getSelectedPropertyId() === openedFor && getSelectedPropertyDetails() === details;
    const showCouldNotOpen = () => toast('error', {
      body: ['Could not open the utility setup. Open it from the property Details tab.']
    });
    try {
      var _store$homeDashboardS4, _details$utilityTempl;
      // Only the Details tab loads the units the setup dialogs read; without them saving meter IDs deletes them all.
      const buildingId = (_store$homeDashboardS4 = store.homeDashboardStore.getCurrentProperty) === null || _store$homeDashboardS4 === void 0 || (_store$homeDashboardS4 = _store$homeDashboardS4.buildingInformation) === null || _store$homeDashboardS4 === void 0 ? void 0 : _store$homeDashboardS4.id;
      if (!buildingId) {
        showCouldNotOpen();
        return;
      }
      const units = await store.units.getUnitsAsync.run({
        buildingId,
        applyToHomeDashboard: true
      }, [propertyId]);
      if (!isSameScreen()) return;
      if (!(units !== null && units !== void 0 && units.isSuccessful)) {
        showCouldNotOpen();
        return;
      }
      const isLoaded = await details.fetchUtilityTemplates({
        forceUpdate: true
      });
      if (!isSameScreen()) return;
      if (isLoaded !== true) {
        showCouldNotOpen();
        return;
      }
      const template = (_details$utilityTempl = details.utilityTemplatesList) === null || _details$utilityTempl === void 0 ? void 0 : _details$utilityTempl.find(entry => entry.utilityId === utilityId);
      if (!template) {
        showCouldNotOpen();
        return;
      }
      details.setUtilityTemplateModalForm(template);
      mappingOpenedDetailsRef.current = details;
      setMappingOpenFor(openedFor);
    } catch (_unused) {
      if (isActiveRef.current) showCouldNotOpen();
    } finally {
      isOpeningMappingRef.current = false;
      if (isActiveRef.current) setIsOpeningMapping(false);
    }
  };
  const handleMappingClosed = () => setMappingOpenFor(null);
  const isMappingOpen = mappingOpenFor === selectedPropertyId && mappingOpenedDetailsRef.current === currentPropertyDetails;
  const _useState5 = useState(null),
    _useState6 = _slicedToArray(_useState5, 2),
    csvMapId = _useState6[0],
    setCsvMapId = _useState6[1];
  const _useState7 = useState(true),
    _useState8 = _slicedToArray(_useState7, 2),
    initialLoading = _useState8[0],
    setInitialLoading = _useState8[1];
  useEffect(() => {
    if (!propertyUtilityId) return;
    columnMapReadsRef.current = getColumnMapVersion(propertyUtilityId);
    store.propertyUtility.getSubmeterConfigV2Async.run({
      propertyUtilityId
    }).then(resp => {
      if (resp !== null && resp !== void 0 && resp.isSuccessful) {
        var _resp$response;
        setCsvMapId(((_resp$response = resp.response) === null || _resp$response === void 0 ? void 0 : _resp$response.csvColumnMapId) || null);
      }
    });
  }, [propertyUtilityId]); // eslint-disable-line react-hooks/exhaustive-deps

  const _useState9 = useState([]),
    _useState0 = _slicedToArray(_useState9, 2),
    parsedRows = _useState0[0],
    setParsedRows = _useState0[1];
  const _useState1 = useState(null),
    _useState10 = _slicedToArray(_useState1, 2),
    parseCsvResponse = _useState10[0],
    setParseCsvResponse = _useState10[1];
  const _useState11 = useState([]),
    _useState12 = _slicedToArray(_useState11, 2),
    parseWarnings = _useState12[0],
    setParseWarnings = _useState12[1];
  const _useState13 = useState(null),
    _useState14 = _slicedToArray(_useState13, 2),
    savedAllocations = _useState14[0],
    setSavedAllocations = _useState14[1];
  const _useState15 = useState(() => new Set()),
    _useState16 = _slicedToArray(_useState15, 2),
    balancedMeters = _useState16[0],
    setBalancedMeters = _useState16[1];
  const applyLoadedRows = rows => {
    setSavedAllocations(rows);
    setBalancedMeters(toBalancedMeterKeys(rows));
  };
  const _useState17 = useState(null),
    _useState18 = _slicedToArray(_useState17, 2),
    variance = _useState18[0],
    setVariance = _useState18[1];
  const _useState19 = useState(false),
    _useState20 = _slicedToArray(_useState19, 2),
    isVarianceLoading = _useState20[0],
    setIsVarianceLoading = _useState20[1];
  const _useState21 = useState(false),
    _useState22 = _slicedToArray(_useState21, 2),
    isVarianceFailed = _useState22[0],
    setIsVarianceFailed = _useState22[1];
  const _useState23 = useState(false),
    _useState24 = _slicedToArray(_useState23, 2),
    isReviewing = _useState24[0],
    setIsReviewing = _useState24[1];
  const _useState25 = useState(null),
    _useState26 = _slicedToArray(_useState25, 2),
    unmappedUnits = _useState26[0],
    setUnmappedUnits = _useState26[1];
  const _useState27 = useState(false),
    _useState28 = _slicedToArray(_useState27, 2),
    isUnmappedConfirmOpen = _useState28[0],
    setIsUnmappedConfirmOpen = _useState28[1];
  const _useState29 = useState(false),
    _useState30 = _slicedToArray(_useState29, 2),
    isCheckingUnmapped = _useState30[0],
    setIsCheckingUnmapped = _useState30[1];
  const varianceReadIdRef = useRef(0);
  const unmappedReadIdRef = useRef(0);
  const unmappedReadRef = useRef(null);
  const unmappedAnswerRef = useRef(null);
  const liveCycleRef = useRef({});
  const utilityRef = useRef(utility);
  const unmappedContextRef = useRef(null);
  utilityRef.current = utility;
  const bindGenerationRef = useRef(0);
  const cycleKeyRef = useRef("".concat(propertyId, ":").concat(utilityId, ":").concat(billingCycleId));
  const cycleEpochRef = useRef(0);
  // Advances whenever the cycle's bill goes or comes back, so an approval answered across that change is not locked in.
  const billVersionRef = useRef(0);
  const _useState31 = useState(false),
    _useState32 = _slicedToArray(_useState31, 2),
    rowsLoadFailed = _useState32[0],
    setRowsLoadFailed = _useState32[1];
  const _useState33 = useState(false),
    _useState34 = _slicedToArray(_useState33, 2),
    isApproved = _useState34[0],
    setIsApproved = _useState34[1];
  // Locks the grid while the PUT and approve run, so no edit lands between them.
  const _useState35 = useState(false),
    _useState36 = _slicedToArray(_useState35, 2),
    isSaving = _useState36[0],
    setIsSaving = _useState36[1];
  const _useState37 = useState(false),
    _useState38 = _slicedToArray(_useState37, 2),
    isApproveFlowRunning = _useState38[0],
    setIsApproveFlowRunning = _useState38[1];
  const _useState39 = useState(false),
    _useState40 = _slicedToArray(_useState39, 2),
    isRecalculateConfirmOpen = _useState40[0],
    setIsRecalculateConfirmOpen = _useState40[1];
  const _useState41 = useState(false),
    _useState42 = _slicedToArray(_useState41, 2),
    isRecalculatedStale = _useState42[0],
    setIsRecalculatedStale = _useState42[1];
  const _useState43 = useState(false),
    _useState44 = _slicedToArray(_useState43, 2),
    isRefreshingRecalculatedRows = _useState44[0],
    setIsRefreshingRecalculatedRows = _useState44[1];
  const rebuildKey = "".concat(propertyId, ":").concat(utilityId, ":").concat(billingCycleId);
  const isRecalculating = useSyncExternalStore(subscribeToCycleWork, () => rebuildsInFlight.has(rebuildKey));
  const isRowWriteRunning = useSyncExternalStore(subscribeToCycleWork, () => rowWritesInFlight.has(rebuildKey));
  const isFirstReadDeferredRef = useRef(false);
  const _useState45 = useState(false),
    _useState46 = _slicedToArray(_useState45, 2),
    mustConfirmRows = _useState46[0],
    setMustConfirmRows = _useState46[1];
  const ranRebuildHereRef = useRef(false);
  const wasRecalculatingRef = useRef(false);
  const loadVariance = async () => {
    if (!isActiveRef.current) return null;
    const readId = ++varianceReadIdRef.current;
    setIsVarianceLoading(true);
    const resp = await store.allocations.getSubmeterBillingAllocationsVarianceAsync.run({
      propertyId,
      utilityId,
      billingCycleId
    });
    if (readId !== varianceReadIdRef.current) return null;
    setIsVarianceLoading(false);
    const landed = resp !== null && resp !== void 0 && resp.isSuccessful ? resp.response : null;
    if (typeof (landed === null || landed === void 0 ? void 0 : landed.requiresAcknowledgement) !== 'boolean') {
      setVariance(null);
      setIsVarianceFailed(true);
      return null;
    }
    setVariance(landed);
    setIsVarianceFailed(false);
    return landed;
  };
  const loadUnmappedUnits = () => {
    if (!isActiveRef.current || cycleKeyRef.current !== "".concat(propertyId, ":").concat(utilityId, ":").concat(billingCycleId)) {
      return Promise.resolve();
    }
    const readId = ++unmappedReadIdRef.current;
    const read = store.allocations.getSubmeterBillingAllocationsUnmappedBillableUnitsAsync.run({
      propertyId,
      utilityId,
      billingCycleId
    }).then(resp => {
      if (readId !== unmappedReadIdRef.current) return;
      const answer = resp !== null && resp !== void 0 && resp.isSuccessful && Array.isArray(resp.response) ? resp.response : null;
      unmappedAnswerRef.current = answer;
      setUnmappedUnits(answer);
    });
    unmappedReadRef.current = read;
    return read;
  };
  const settledUnmappedUnits = async () => {
    let read;
    do {
      read = unmappedReadRef.current;
      await read;
    } while (read !== unmappedReadRef.current);
    return unmappedAnswerRef.current;
  };
  const loadSavedRows = () => {
    const epoch = cycleEpochRef.current;
    setRowsLoadFailed(false);
    setInitialLoading(true);
    return store.allocations.getSubmeterBillingAllocationsAsync.run({
      propertyId,
      utilityId,
      billingCycleId
    }).then(resp => {
      if (epoch !== cycleEpochRef.current) return;
      if (!(resp !== null && resp !== void 0 && resp.isSuccessful) || !Array.isArray(resp.response)) {
        setRowsLoadFailed(true);
        loadUnmappedUnits();
        return;
      }
      setMustConfirmRows(false);
      if (resp.response.length > 0) {
        applyLoadedRows(resp.response);
        loadVariance();
        loadUnmappedUnits();
      } else {
        onNoRows === null || onNoRows === void 0 || onNoRows();
        loadUnmappedUnits();
      }
    }).finally(() => {
      if (epoch === cycleEpochRef.current) setInitialLoading(false);
    });
  };
  useEffect(() => {
    if (!propertyId || !utilityId || !billingCycleId) {
      setInitialLoading(false);
      return;
    }
    cycleEpochRef.current += 1;
    cycleKeyRef.current = "".concat(propertyId, ":").concat(utilityId, ":").concat(billingCycleId);
    varianceReadIdRef.current += 1;
    unmappedReadIdRef.current += 1;
    unmappedReadRef.current = null;
    unmappedAnswerRef.current = null;
    setVariance(null);
    setIsVarianceLoading(false);
    setIsVarianceFailed(false);
    setUnmappedUnits(null);
    setIsUnmappedConfirmOpen(false);
    setIsApproved(false);
    setIsRefreshingRecalculatedRows(false);
    wasRecalculatingRef.current = false;
    ranRebuildHereRef.current = false;
    // Rows read while another screen's row write runs could predate it, and a Save from them would undo it.
    if (rowWritesInFlight.has(rebuildKey)) {
      isFirstReadDeferredRef.current = true;
      setMustConfirmRows(true);
      setInitialLoading(true);
      return;
    }
    isFirstReadDeferredRef.current = false;
    setMustConfirmRows(false);
    loadSavedRows();
  }, [propertyId, utilityId, billingCycleId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isRowWriteRunning || !isFirstReadDeferredRef.current) return;
    isFirstReadDeferredRef.current = false;
    loadSavedRows();
  }, [isRowWriteRunning]); // eslint-disable-line react-hooks/exhaustive-deps

  const currentCompany = store.homeDashboardStore.getCurrentCompany || {};
  const applyBill = currentCompany.billingTab.applyBill || {};
  const allocationCycleId = applyBill.allocationCycleId;
  // After the statement is sent out the meter details are read-only, no re-save/approve.
  const isStatementSent = applyBill.status === 'TableReadyStatementSent';
  // The server's table read leaves the utility's live bill empty once the cycle has none. Nothing is offered then.
  const isBillRemoved = !!utility && !utility.bill;
  const isCycleApproved = isApproved || applyBill.status === 'TableReady' || isStatementSent;
  useEffect(() => {
    billVersionRef.current += 1;
    if (isBillRemoved) {
      setIsApproved(false);
      setIsRecalculateConfirmOpen(false);
      closeOldServicePeriodRef.current();
    }
  }, [isBillRemoved]);
  const billKey = "".concat(utility === null || utility === void 0 || (_utility$bill = utility.bill) === null || _utility$bill === void 0 ? void 0 : _utility$bill.id, ":").concat(utility === null || utility === void 0 || (_utility$bill2 = utility.bill) === null || _utility$bill2 === void 0 ? void 0 : _utility$bill2.startDateOnly, ":").concat(utility === null || utility === void 0 || (_utility$bill3 = utility.bill) === null || _utility$bill3 === void 0 ? void 0 : _utility$bill3.endDateOnly);
  const billKeyRef = useRef(billKey);
  useEffect(() => {
    if (billKeyRef.current === billKey) return;
    billKeyRef.current = billKey;
    bindGenerationRef.current += 1;
    if (!isBillRemoved) loadUnmappedUnits();
  }, [billKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const billUsageUnits = toUsageUnits(billUsage);
  const renderUsageWarning = meteredUnits => !isStatementSent && billUsageUnits > 0 && meteredUnits > billUsageUnits ? /*#__PURE__*/React.createElement("div", {
    className: classes.usageWarning
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.usageWarningContent,
    role: "status"
  }, /*#__PURE__*/React.createElement(Icon, {
    type: "warning",
    state: "warning"
  }), /*#__PURE__*/React.createElement("span", null, "Metered usage (".concat(formatUsageUnits(meteredUnits), ") is more than the bill usage (").concat(formatUsageUnits(billUsageUnits), "). Check Bill usage on the Bill details tab or the uploaded reads.")))) : null;
  const renderMeterTotalWarnings = meterRows => isStatementSent || isBillRemoved ? null : toMeterTotalMismatches(meterRows, balancedMeters).map(_ref3 => {
    let key = _ref3.key,
      message = _ref3.message;
    return /*#__PURE__*/React.createElement("div", {
      key: key,
      className: classes.usageWarning
    }, /*#__PURE__*/React.createElement("div", {
      className: classes.usageWarningContent,
      role: "status"
    }, /*#__PURE__*/React.createElement(Icon, {
      type: "warning",
      state: "warning"
    }), /*#__PURE__*/React.createElement("span", null, message)));
  });
  const rows = parsedRows.map((row, idx) => {
    var _row$unitName, _row$residentFullName, _row$meterSerialNumbe2, _row$startMeterRead, _row$endMeterRead, _row$startMeterDateOn, _row$endMeterDateOnly, _row$meterUsage, _row$meterRate, _row$unitsOfMeasure, _row$allocationCharge, _row$serviceDaysInPer, _row$isRateOverridden;
    return {
      id: idx,
      unitName: (_row$unitName = row.unitName) !== null && _row$unitName !== void 0 ? _row$unitName : '',
      residentFullName: (_row$residentFullName = row.residentFullName) !== null && _row$residentFullName !== void 0 ? _row$residentFullName : '',
      meterSerialNumber: (_row$meterSerialNumbe2 = row.meterSerialNumber) !== null && _row$meterSerialNumbe2 !== void 0 ? _row$meterSerialNumbe2 : '',
      startMeterRead: (_row$startMeterRead = row.startMeterRead) !== null && _row$startMeterRead !== void 0 ? _row$startMeterRead : '',
      endMeterRead: (_row$endMeterRead = row.endMeterRead) !== null && _row$endMeterRead !== void 0 ? _row$endMeterRead : '',
      startMeterDateOnly: (_row$startMeterDateOn = row.startMeterDateOnly) !== null && _row$startMeterDateOn !== void 0 ? _row$startMeterDateOn : '',
      endMeterDateOnly: (_row$endMeterDateOnly = row.endMeterDateOnly) !== null && _row$endMeterDateOnly !== void 0 ? _row$endMeterDateOnly : '',
      meterUsage: (_row$meterUsage = row.meterUsage) !== null && _row$meterUsage !== void 0 ? _row$meterUsage : '',
      meterRate: (_row$meterRate = row.meterRate) !== null && _row$meterRate !== void 0 ? _row$meterRate : '',
      unitsOfMeasure: (_row$unitsOfMeasure = row.unitsOfMeasure) !== null && _row$unitsOfMeasure !== void 0 ? _row$unitsOfMeasure : '',
      allocationCharge: (_row$allocationCharge = row.allocationCharge) !== null && _row$allocationCharge !== void 0 ? _row$allocationCharge : '',
      serviceDaysInPeriod: (_row$serviceDaysInPer = row.serviceDaysInPeriod) !== null && _row$serviceDaysInPer !== void 0 ? _row$serviceDaysInPer : '',
      isRateOverridden: (_row$isRateOverridden = row.isRateOverridden) !== null && _row$isRateOverridden !== void 0 ? _row$isRateOverridden : false
    };
  });
  const blockIfReadsMissing = rowsToSend => {
    const units = findUnitsMissingReads(rowsToSend);
    if (!units.length) return false;
    const names = units.map(name => name ? t('allocations.meter_details_missing_reads_unit', {
      name
    }) : t('allocations.meter_details_missing_reads_unnamed_unit'));
    toast('error', {
      body: [t('allocations.meter_details_missing_reads', {
        units: names.join(', ')
      })]
    });
    return true;
  };
  const processRowUpdate = (newRow, oldRow) => {
    // The grid calls this on every committed cell exit, even when nothing was typed, so whether the
    // rate was overridden is taken from the grid's own before/after pair. Comparing the cell against
    // the parsed CSV instead would report edits the manager never made, and a false override is not
    // cosmetic: the server prices the override on the row it was typed on rather than grouping by
    // unit.
    const _resolveRateEdit = resolveRateEdit(oldRow.meterRate, newRow.meterRate, oldRow.isRateOverridden),
      isValid = _resolveRateEdit.isValid,
      isRateOverridden = _resolveRateEdit.isRateOverridden;
    if (!isValid) {
      toast('error', {
        body: ['A rate override has to be a number of zero or more and below 1,000,000,000.']
      });
      return oldRow;
    }
    track(events.changed('Parsed Meter Row'), {
      page,
      unit: newRow.unitName
    });

    // The grid renders from the row this returns, not from parsedRows, so the flag has to travel on
    // the returned row or the marker never reaches the cell.
    const readsChanged = haveReadsChanged(newRow, oldRow);
    const updatedRow = _objectSpread(_objectSpread({}, newRow), {}, {
      isRateOverridden
    }, readsChanged && {
      meterUsage: toUsageFromReads(newRow.startMeterRead, newRow.endMeterRead)
    });
    setParsedRows(prev => prev.map((row, idx) => idx === newRow.id ? _objectSpread(_objectSpread({}, row), updatedRow) : row));
    return updatedRow;
  };
  const notifyParseOutcome = (rowCount, warnings) => {
    if (rowCount === 0 && warnings.some(w => w.code === 'missing_required_column')) {
      toast('error', {
        body: ['This file is missing a required column. See the messages above the table.']
      });
      return;
    }
    if (rowCount === 0 && warnings.length === 0) {
      toast('error', {
        body: ['This file has no data rows.']
      });
      return;
    }
    if (rowCount === 0) {
      const unmatchedOnly = warnings.every(w => UNMATCHED_PARSE_CODES.includes(w.code));
      toast('error', {
        body: [unmatchedOnly ? 'No rows matched the units on this utility. See the messages above the table.' : 'No rows could be read from this file. See the messages above the table.']
      });
      return;
    }
    if (warnings.length === 0) {
      toast('success', {
        body: ['CSV parsed successfully!']
      });
      return;
    }
    const skipped = warnings.filter(w => SKIPPED_PARSE_CODES.includes(w.code)).length;
    const parsed = "".concat(rowCount, " ").concat(rowCount === 1 ? 'row' : 'rows');
    toast('warning', {
      body: ["Parsed ".concat(parsed, ", ").concat(skipped, " skipped. See the messages above the table.")]
    });
  };
  const handleCsvUpload = async e => {
    var _e$target$files;
    const file = (_e$target$files = e.target.files) === null || _e$target$files === void 0 ? void 0 : _e$target$files[0];
    if (!file) return;
    const uploadedFor = getSelectedPropertyId();
    const generation = selectionRef.current.generation;
    uploadSeqRef.current += 1;
    const uploadSeq = uploadSeqRef.current;
    const isStillHere = () => isActiveRef.current && uploadSeqRef.current === uploadSeq && selectionRef.current.generation === generation && getSelectedPropertyId() === uploadedFor;
    try {
      var _resp$response2, _resp$response$warnin;
      const csvContent = await file.text();
      if (!isStillHere()) return;
      let uploadCsvMapId = csvMapId;
      const changesSeen = getColumnMapVersion(propertyUtilityId);
      if (changesSeen !== columnMapReadsRef.current && propertyUtilityId) {
        var _config$response;
        const config = await store.propertyUtility.getSubmeterConfigV2Async.run({
          propertyUtilityId
        });
        if (!isStillHere()) return;
        if (!(config !== null && config !== void 0 && config.isSuccessful)) {
          toast('error', {
            body: ['Could not check the column mapping. Try the upload again.']
          });
          return;
        }
        columnMapReadsRef.current = changesSeen;
        uploadCsvMapId = ((_config$response = config.response) === null || _config$response === void 0 ? void 0 : _config$response.csvColumnMapId) || null;
        setCsvMapId(uploadCsvMapId);
      }
      if (!isStillHere()) return;
      const resp = await store.allocations.postSubmeterBillingParseCsvAsync.run({
        propertyId,
        utilityId,
        csvMapId: uploadCsvMapId,
        billingCycleId,
        csvContent,
        isFirstCycleSecondFile: false
      });
      if (!isStillHere()) return;
      if (!(resp !== null && resp !== void 0 && resp.isSuccessful) || !Array.isArray((_resp$response2 = resp.response) === null || _resp$response2 === void 0 ? void 0 : _resp$response2.rows)) return;
      // Parse always returns meterRate: 0, a placeholder the server replaces at save. Normalising it
      // to blank here, once, is what lets a manager type 0 later and have it register as an edit.
      const normalizedRows = resp.response.rows.map(row => _objectSpread(_objectSpread({}, row), {}, {
        meterRate: toInitialMeterRate(row.meterRate),
        meterUsage: toUsageFromReads(row.startMeterRead, row.endMeterRead)
      }));
      const warnings = (_resp$response$warnin = resp.response.warnings) !== null && _resp$response$warnin !== void 0 ? _resp$response$warnin : [];
      setParsedRows(normalizedRows);
      setParseCsvResponse(resp.response);
      setParseWarnings(warnings);
      track(events.fileUploaded('Meter Reads CSV'), {
        page,
        rowsCount: resp.response.rows.length
      });
      notifyParseOutcome(resp.response.rows.length, warnings);
    } catch (_unused2) {
      toast('error', {
        body: ['Failed to parse CSV']
      });
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // Reload after a write so the grid shows the backend's rounding, not the typed values.
  const reloadRows = async () => {
    if (!isActiveRef.current) return;
    const allocResp = await store.allocations.getSubmeterBillingAllocationsAsync.run({
      propertyId,
      utilityId,
      billingCycleId
    });
    if (allocResp !== null && allocResp !== void 0 && allocResp.isSuccessful && Array.isArray(allocResp.response)) {
      applyLoadedRows(allocResp.response);
    }
  };
  const handleSave = async () => {
    if (!parsedRows.length) return;
    if (blockIfReadsMissing(parsedRows)) return;
    track(events.clicked('Add Bill To Meter'), {
      page,
      rowsCount: parsedRows.length
    });
    const saveRows = toSaveRows(parsedRows);
    const resp = await trackRowWrite(rebuildKey, () => store.allocations.postSubmeterBillingAllocationsSaveAsync.run({
      propertyId,
      utilityId,
      billingCycleId,
      allocationCycleId,
      userInitialCsvFileId: (parseCsvResponse === null || parseCsvResponse === void 0 ? void 0 : parseCsvResponse.csvFileId) || '',
      userEditedCsvFileId: (parseCsvResponse === null || parseCsvResponse === void 0 ? void 0 : parseCsvResponse.csvFileId) || '',
      acknowledgeAppr: true,
      cycleDisclosure: (parseCsvResponse === null || parseCsvResponse === void 0 ? void 0 : parseCsvResponse.cycleDisclosure) || {
        providerPhone: '',
        billingAgentDisclosure: '',
        leakServiceContact: ''
      },
      rows: saveRows
    }));
    if (resp !== null && resp !== void 0 && resp.isSuccessful) {
      toast('success', {
        body: ['Allocations saved!']
      });
      await reloadRows();
      onRowsSaved === null || onRowsSaved === void 0 || onRowsSaved();
      loadVariance();
      loadUnmappedUnits();
    }
  };
  const columns = [{
    field: 'unitName',
    headerName: 'Unit',
    width: 80,
    cellClassName: 'cell-readonly',
    sortComparator: (v1, v2) => ('' + v1).localeCompare(v2, undefined, {
      numeric: true
    })
  }, {
    field: 'residentFullName',
    headerName: 'Resident',
    flex: 1,
    minWidth: 150,
    cellClassName: 'cell-readonly'
  }, {
    field: 'meterSerialNumber',
    headerName: 'Meter #',
    width: 100,
    cellClassName: 'cell-readonly'
  }, {
    field: 'startMeterDateOnly',
    headerName: 'Start date',
    width: 120,
    editable: true,
    valueFormatter: params => params.value ? moment(params.value).format('MM/DD/YY') : ''
  }, {
    field: 'endMeterDateOnly',
    headerName: 'End date',
    width: 120,
    editable: true,
    valueFormatter: params => params.value ? moment(params.value).format('MM/DD/YY') : ''
  }, {
    field: 'startMeterRead',
    headerName: 'Start read',
    width: 100,
    type: 'number',
    editable: true,
    align: 'left',
    headerAlign: 'left'
  }, {
    field: 'endMeterRead',
    headerName: 'End read',
    width: 100,
    type: 'number',
    editable: true,
    align: 'left',
    headerAlign: 'left'
  }, {
    field: 'meterUsage',
    headerName: 'Usage',
    width: 100,
    type: 'number',
    editable: true,
    align: 'left',
    headerAlign: 'left',
    valueFormatter: params => formatUsage(params.value)
  }, {
    field: 'meterRate',
    headerName: 'Rate',
    width: 80,
    type: 'number',
    editable: true,
    align: 'left',
    headerAlign: 'left',
    cellClassName: params => params.row.isRateOverridden ? 'cell-rate-overridden' : '',
    valueFormatter: params => formatMeterRate(params.value)
  }, {
    field: 'unitsOfMeasure',
    headerName: 'UOM',
    width: 80,
    editable: true
  },
  // Charge and service days are recomputed on save from reads, bill and residency dates, so a
  // typed value here never reached the server. Read-only until the backend accepts them (LB-5837).
  {
    field: 'allocationCharge',
    headerName: 'Charge',
    width: 100,
    type: 'number',
    cellClassName: 'cell-readonly',
    align: 'left',
    headerAlign: 'left',
    valueFormatter: params => formatMoney(params.value)
  }, {
    field: 'serviceDaysInPeriod',
    headerName: 'Service days',
    width: 110,
    type: 'number',
    cellClassName: 'cell-readonly',
    align: 'left',
    headerAlign: 'left'
  }];
  const finalRows = (savedAllocations || []).map((row, idx) => {
    var _row$id, _row$residencyId, _row$overrideRate, _row$overrideRate2, _row$unitId, _row$unitName2, _row$residentFullName2, _row$meterSerialNumbe3, _row$startMeterRead2, _row$endMeterRead2, _row$startMeterDateOn2, _row$endMeterDateOnly2, _row$meterUsage2, _row$meterRate2, _row$baseCharge, _row$usageCharge, _row$allocationCharge2, _row$serviceDaysInPer2, _row$unitsOfMeasure2;
    return {
      id: idx,
      allocationId: (_row$id = row.id) !== null && _row$id !== void 0 ? _row$id : '',
      residencyId: (_row$residencyId = row.residencyId) !== null && _row$residencyId !== void 0 ? _row$residencyId : '',
      overrideRate: (_row$overrideRate = row.overrideRate) !== null && _row$overrideRate !== void 0 ? _row$overrideRate : null,
      isRateOverridden: ((_row$overrideRate2 = row.overrideRate) !== null && _row$overrideRate2 !== void 0 ? _row$overrideRate2 : null) !== null,
      unitId: (_row$unitId = row.unitId) !== null && _row$unitId !== void 0 ? _row$unitId : '',
      unitName: (_row$unitName2 = row.unitName) !== null && _row$unitName2 !== void 0 ? _row$unitName2 : '',
      residentFullName: (_row$residentFullName2 = row.residentFullName) !== null && _row$residentFullName2 !== void 0 ? _row$residentFullName2 : '',
      meterSerialNumber: (_row$meterSerialNumbe3 = row.meterSerialNumber) !== null && _row$meterSerialNumbe3 !== void 0 ? _row$meterSerialNumbe3 : '',
      startMeterRead: (_row$startMeterRead2 = row.startMeterRead) !== null && _row$startMeterRead2 !== void 0 ? _row$startMeterRead2 : '',
      endMeterRead: (_row$endMeterRead2 = row.endMeterRead) !== null && _row$endMeterRead2 !== void 0 ? _row$endMeterRead2 : '',
      startMeterDateOnly: (_row$startMeterDateOn2 = row.startMeterDateOnly) !== null && _row$startMeterDateOn2 !== void 0 ? _row$startMeterDateOn2 : '',
      endMeterDateOnly: (_row$endMeterDateOnly2 = row.endMeterDateOnly) !== null && _row$endMeterDateOnly2 !== void 0 ? _row$endMeterDateOnly2 : '',
      meterUsage: (_row$meterUsage2 = row.meterUsage) !== null && _row$meterUsage2 !== void 0 ? _row$meterUsage2 : '',
      meterRate: (_row$meterRate2 = row.meterRate) !== null && _row$meterRate2 !== void 0 ? _row$meterRate2 : '',
      baseCharge: (_row$baseCharge = row.baseCharge) !== null && _row$baseCharge !== void 0 ? _row$baseCharge : '',
      usageCharge: (_row$usageCharge = row.usageCharge) !== null && _row$usageCharge !== void 0 ? _row$usageCharge : '',
      allocationCharge: (_row$allocationCharge2 = row.allocationCharge) !== null && _row$allocationCharge2 !== void 0 ? _row$allocationCharge2 : '',
      serviceDaysInPeriod: (_row$serviceDaysInPer2 = row.serviceDaysInPeriod) !== null && _row$serviceDaysInPer2 !== void 0 ? _row$serviceDaysInPer2 : '',
      unitsOfMeasure: (_row$unitsOfMeasure2 = row.unitsOfMeasure) !== null && _row$unitsOfMeasure2 !== void 0 ? _row$unitsOfMeasure2 : ''
    };
  });
  const processFinalRowUpdate = (rawRow, oldRow) => {
    var _oldRow$overrideRate2, _derivedGroup$find;
    const _resolveRateEdit2 = resolveRateEdit(oldRow.meterRate, rawRow.meterRate, oldRow.isRateOverridden),
      isValid = _resolveRateEdit2.isValid;
    if (!isValid) {
      toast('error', {
        body: ['A rate override has to be a number of zero or more and below 1,000,000,000.']
      });
      return oldRow;
    }
    const rateEdited = rawRow.meterRate !== oldRow.meterRate;
    const overrideRate = rateEdited ? toOverrideRate(rawRow.meterRate) : (_oldRow$overrideRate2 = oldRow.overrideRate) !== null && _oldRow$overrideRate2 !== void 0 ? _oldRow$overrideRate2 : null;
    const newRow = _objectSpread(_objectSpread({}, rawRow), {}, {
      meterRate: rateEdited ? overrideRate !== null && overrideRate !== void 0 ? overrideRate : oldRow.meterRate : rawRow.meterRate,
      overrideRate,
      isRateOverridden: overrideRate !== null
    });
    track(events.changed('Saved Meter Row'), {
      page,
      unit: newRow.unitName
    });
    // Raw savedAllocations rows can carry meterSerialNumber as null; the grid's own rows always
    // coalesce it to ''. Comparing the two shapes directly leaves the edited row's raw copy out of
    // its own group, so both sides are normalised the same way here before comparing.
    const meterKey = row => {
      var _row$meterSerialNumbe4;
      return "".concat(row.unitId, "::").concat((_row$meterSerialNumbe4 = row.meterSerialNumber) !== null && _row$meterSerialNumbe4 !== void 0 ? _row$meterSerialNumbe4 : '');
    };
    const meterGroup = (savedAllocations || []).map((row, idx) => _objectSpread(_objectSpread({}, row), {}, {
      id: idx,
      allocationId: row.id
    })).filter(row => meterKey(row) === meterKey(newRow));
    const derivedGroup = deriveMeterGroupCharges(newRow, oldRow, meterGroup);
    setSavedAllocations(prev => prev.map((row, idx) => {
      const match = derivedGroup.find(d => d.id === idx);
      return match ? _objectSpread(_objectSpread(_objectSpread({}, row), match), {}, {
        id: row.id
      }) : row;
    }));
    return (_derivedGroup$find = derivedGroup.find(d => d.id === newRow.id)) !== null && _derivedGroup$find !== void 0 ? _derivedGroup$find : newRow;
  };
  const persistRows = async () => {
    const resp = await trackRowWrite(rebuildKey, () => store.allocations.putSubmeterBillingAllocationsAsync.run({
      rows: toUpdateRows(savedAllocations)
    }));
    if (!(resp !== null && resp !== void 0 && resp.isSuccessful)) return false;
    await reloadRows();
    onRowsSaved === null || onRowsSaved === void 0 || onRowsSaved();
    return true;
  };
  const handleFinalSave = async () => {
    if (!(savedAllocations !== null && savedAllocations !== void 0 && savedAllocations.length)) return;
    if (blockIfReadsMissing(savedAllocations)) return;
    track(events.clicked('Save Meter Allocations'), {
      page,
      rowsCount: savedAllocations.length
    });
    setIsSaving(true);
    try {
      if (await persistRows()) {
        toast('success', {
          body: ['Allocations updated!']
        });
        loadUnmappedUnits();
        await loadVariance();
      }
    } finally {
      setIsSaving(false);
    }
  };
  const reviewStore = store.allocations.postSubmeterBillingAllocationsReviewAsync;
  const approveStore = store.allocations.postSubmeterBillingAllocationsApproveAsync;
  const handleReview = async checked => {
    if (!checked || !variance || isReviewing) return;
    track(events.enabled('Variance Reviewed'), {
      page
    });
    const epoch = cycleEpochRef.current;
    setIsReviewing(true);
    try {
      var _reviewStore$error;
      const resp = await reviewStore.run({
        propertyId,
        utilityId,
        billingCycleId,
        draftRevision: variance.draftRevision
      });
      if (resp !== null && resp !== void 0 && resp.isSuccessful) {
        if (epoch === cycleEpochRef.current) setVariance(prev => _objectSpread(_objectSpread({}, prev), {}, {
          isReviewed: true
        }));
      } else if (((_reviewStore$error = reviewStore.error) === null || _reviewStore$error === void 0 ? void 0 : _reviewStore$error.errorCode) === DRAFT_CHANGED) {
        await loadVariance();
      } else {
        toastFailure(reviewStore);
      }
    } finally {
      setIsReviewing(false);
    }
  };
  const approveAndSave = async () => {
    const epoch = cycleEpochRef.current;
    const billVersion = billVersionRef.current;
    setIsApproveFlowRunning(true);
    try {
      var _approveStore$error;
      track(events.clicked('Approve And Save Meter Allocations'), {
        page
      });
      if (!(savedAllocations !== null && savedAllocations !== void 0 && savedAllocations.length) || !(await persistRows())) return;

      // A row change drops a stored review, so approve is decided from a fresh read.
      const fresh = await loadVariance();
      if (!fresh || fresh.requiresAcknowledgement && !fresh.isReviewed) return;
      if (!isActiveRef.current || billVersion !== billVersionRef.current) return;
      const resp = await approveStore.run({
        propertyId,
        utilityId,
        billingCycleId
      });
      if (resp !== null && resp !== void 0 && resp.isSuccessful) {
        if (epoch === cycleEpochRef.current && billVersion === billVersionRef.current) setIsApproved(true);
        toast('success', {
          body: ['Allocations approved!']
        });
        onApproved === null || onApproved === void 0 || onApproved();
        return;
      }
      if (((_approveStore$error = approveStore.error) === null || _approveStore$error === void 0 ? void 0 : _approveStore$error.errorCode) === ACKNOWLEDGEMENT_REQUIRED) {
        await loadVariance();
      } else {
        toastFailure(approveStore);
      }
    } finally {
      setIsApproveFlowRunning(false);
    }
  };
  const unmappedSummary = unmappedUnits !== null && unmappedUnits !== void 0 && unmappedUnits.length ? "".concat(unmappedUnits.length, " billable ").concat(unmappedUnits.length === 1 ? 'unit has' : 'units have', " no mapping and will not be charged: ").concat(unmappedUnits.map(unit => unit.unitName || 'Unnamed unit').join(', '), ".") : null;
  const unmappedNotice = isBillRemoved ? null : unmappedSummary;
  const isOtherWorkRunning = () => store.allocations.putSubmeterBillingAllocationsAsync.inProgress || store.allocations.postSubmeterBillingAllocationsSaveAsync.inProgress || approveStore.inProgress || reviewStore.inProgress || rebuildsInFlight.has(rebuildKey) || rowWritesInFlight.has(rebuildKey) || approvalsInFlight.has(rebuildKey);
  const startApproval = () => trackApproval(rebuildKey, approveAndSave);
  const liveBillKey = () => {
    var _utilityRef$current;
    const bill = (_utilityRef$current = utilityRef.current) === null || _utilityRef$current === void 0 ? void 0 : _utilityRef$current.bill;
    return "".concat(bill === null || bill === void 0 ? void 0 : bill.id, ":").concat(bill === null || bill === void 0 ? void 0 : bill.startDateOnly, ":").concat(bill === null || bill === void 0 ? void 0 : bill.endDateOnly);
  };
  const isCycleClosedNow = () => {
    var _store$homeDashboardS5;
    const status = (_store$homeDashboardS5 = store.homeDashboardStore.getCurrentCompany) === null || _store$homeDashboardS5 === void 0 || (_store$homeDashboardS5 = _store$homeDashboardS5.billingTab) === null || _store$homeDashboardS5 === void 0 || (_store$homeDashboardS5 = _store$homeDashboardS5.applyBill) === null || _store$homeDashboardS5 === void 0 ? void 0 : _store$homeDashboardS5.status;
    const liveUtility = utilityRef.current;
    return status === 'TableReady' || status === 'TableReadyStatementSent' || !!liveUtility && !liveUtility.bill;
  };
  const canApproveNow = () => {
    const live = liveCycleRef.current;
    return isActiveRef.current && !isCycleClosedNow() && !live.isBillRemoved && !live.isCycleApproved && !live.isStatementSent && !live.isWorkRunning && !isOtherWorkRunning();
  };
  const approvalContext = () => "".concat(cycleEpochRef.current, ":").concat(billVersionRef.current, ":").concat(bindGenerationRef.current, ":").concat(liveBillKey());
  const askAboutUnmappedUnits = async context => {
    setIsCheckingUnmapped(true);
    try {
      const units = await settledUnmappedUnits();
      if (context !== approvalContext() || !canApproveNow()) return;
      if (units !== null && units !== void 0 && units.length) {
        unmappedContextRef.current = context;
        setIsUnmappedConfirmOpen(true);
        return;
      }
      startApproval();
    } finally {
      setIsCheckingUnmapped(false);
    }
  };
  const handleConfirmUnmapped = () => {
    if (!isUnmappedConfirmOpen) return undefined;
    setIsUnmappedConfirmOpen(false);
    const openedIn = unmappedContextRef.current;
    unmappedContextRef.current = null;
    if (openedIn !== approvalContext() || !canApproveNow()) return undefined;
    return startApproval();
  };
  useEffect(() => {
    setIsUnmappedConfirmOpen(false);
  }, [billKey, isBillRemoved, isCycleApproved, isStatementSent]);
  useEffect(() => {
    if (!unmappedNotice) setIsUnmappedConfirmOpen(false);
  }, [unmappedNotice]);

  // LB-6030: warns again before approving a long-past service period, but records nothing; the confirmation kept is
  // the one given when it was allocated.
  const handleApproveAndSave = () => {
    if (blockIfReadsMissing(savedAllocations || [])) return;
    const context = approvalContext();
    oldServicePeriod.confirmIfOld([servicePeriod], () => askAboutUnmappedUnits(context), {
      confirmLabel: 'Approve & save',
      title: 'Approve a bill for a long-past period?'
    });
  };

  // These writes outlive a closed screen, so a reopened screen reads their locks from the store.
  const isStoreWriteRunning = store.allocations.putSubmeterBillingAllocationsAsync.inProgress || store.allocations.postSubmeterBillingAllocationsSaveAsync.inProgress || approveStore.inProgress || reviewStore.inProgress;
  const isGridLocked = isSaving || isApproveFlowRunning || isCheckingUnmapped || isStoreWriteRunning || isRecalculating || isRefreshingRecalculatedRows || isRecalculatedStale || isBillRemoved || isCycleApproved;
  liveCycleRef.current = {
    isBillRemoved,
    isCycleApproved,
    isStatementSent,
    isWorkRunning: isSaving || isApproveFlowRunning || isRefreshingRecalculatedRows || isRecalculatedStale
  };
  const readSavedRows = async () => {
    const resp = await store.allocations.getSubmeterBillingAllocationsAsync.run({
      propertyId,
      utilityId,
      billingCycleId
    });
    return resp !== null && resp !== void 0 && resp.isSuccessful && Array.isArray(resp.response) ? resp.response : null;
  };
  const showNoRows = () => {
    setSavedAllocations(null);
    setParsedRows([]);
    setParseCsvResponse(null);
    setParseWarnings([]);
    onNoRows === null || onNoRows === void 0 || onNoRows();
    loadUnmappedUnits();
  };
  const showRecalculatedRows = async () => {
    if (!isActiveRef.current) return;
    const fresh = await readSavedRows();
    if (!fresh) {
      loadUnmappedUnits();
      setIsRecalculatedStale(true);
      return;
    }
    setIsRecalculatedStale(false);
    if (!fresh.length) {
      varianceReadIdRef.current += 1;
      setVariance(null);
      setIsVarianceLoading(false);
      setIsVarianceFailed(false);
      showNoRows();
      toast('success', {
        body: ['Allocations recalculated. No units are left to bill for this cycle.']
      });
      return;
    }
    applyLoadedRows(fresh);
    toast('success', {
      body: ['Allocations recalculated!']
    });
    onRowsSaved === null || onRowsSaved === void 0 || onRowsSaved();
    loadUnmappedUnits();
    await loadVariance();
  };
  const runRecalculateStep = async step => {
    if (isSaving || isApproveFlowRunning || isStoreWriteRunning || isRefreshingRecalculatedRows || isCycleApproved || rebuildsInFlight.has(rebuildKey)) {
      return;
    }
    ranRebuildHereRef.current = true;
    setRebuildInFlight(rebuildKey, true);
    try {
      await step();
    } finally {
      setRebuildInFlight(rebuildKey, false);
    }
  };
  useEffect(() => {
    const finished = wasRecalculatingRef.current && !isRecalculating;
    wasRecalculatingRef.current = isRecalculating;
    // Keep the lock through the row refresh, including the render where the remote rebuild finishes.
    if (isRecalculating && !ranRebuildHereRef.current) setIsRefreshingRecalculatedRows(true);
    if (!finished) return;
    if (ranRebuildHereRef.current) {
      ranRebuildHereRef.current = false;
      return;
    }
    // This read cancels a mount read still in flight; the new epoch makes that read's empty result ignored.
    cycleEpochRef.current += 1;
    const epoch = cycleEpochRef.current;
    readSavedRows().then(fresh => {
      if (epoch !== cycleEpochRef.current) return;
      setInitialLoading(false);
      if (!fresh) {
        loadUnmappedUnits();
        setIsRecalculatedStale(true);
        return;
      }
      setIsRecalculatedStale(false);
      if (fresh.length) {
        applyLoadedRows(fresh);
        loadVariance();
        loadUnmappedUnits();
        return;
      }
      showNoRows();
    }).finally(() => {
      if (epoch === cycleEpochRef.current && !rebuildsInFlight.has(rebuildKey)) setIsRefreshingRecalculatedRows(false);
    });
  }, [isRecalculating, rebuildKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleConfirmRecalculate = () => {
    setIsRecalculateConfirmOpen(false);
    return runRecalculateStep(async () => {
      track(events.clicked('Confirm Recalculate Meter Allocations'), {
        page
      });
      const current = await readSavedRows();
      if (!current) {
        // A failed read has already raised the store's error toast; a cancelled one raises nothing.
        if (!store.allocations.getSubmeterBillingAllocationsAsync.error) {
          toast('error', {
            body: ['The saved rows could not be read, so nothing was recalculated. Try again.']
          });
        }
        return;
      }
      if (!current.length) {
        toast('error', {
          body: ['There are no saved rows to recalculate.']
        });
        return;
      }
      const divergent = findDivergentMeter(current);
      if (divergent) {
        toast('error', {
          body: ["Unit ".concat(divergent.unitName, ", meter ").concat(divergent.meterSerialNumber || '(none)', " has different saved reads on its rows. Make them match and save, then recalculate.")]
        });
        return;
      }
      const resp = await store.allocations.postSubmeterBillingAllocationsSaveAsync.run(toRecalculateRequest(current, {
        propertyId,
        utilityId,
        billingCycleId,
        allocationCycleId
      }));
      if (!(resp !== null && resp !== void 0 && resp.isSuccessful)) return;
      await showRecalculatedRows();
    });
  };
  const handleReloadRecalculatedRows = () => runRecalculateStep(showRecalculatedRows);
  const isRebuildPending = isRecalculating || isRefreshingRecalculatedRows || isRecalculatedStale;
  useEffect(() => {
    onBusy === null || onBusy === void 0 || onBusy(isRebuildPending);
    return () => onBusy === null || onBusy === void 0 ? void 0 : onBusy(false);
  }, [isRebuildPending]); // eslint-disable-line react-hooks/exhaustive-deps

  const isVarianceBusy = isVarianceLoading || isReviewing || isGridLocked;
  const splitShares = toSplitRowShares(finalRows, {
    splitCents: splitCentsByDays,
    compareRows: byResidencyThenRow
  });
  const shownValue = (field, id, value) => {
    var _splitShares$get$fiel, _splitShares$get;
    return (_splitShares$get$fiel = (_splitShares$get = splitShares.get(id)) === null || _splitShares$get === void 0 ? void 0 : _splitShares$get[field]) !== null && _splitShares$get$fiel !== void 0 ? _splitShares$get$fiel : value;
  };
  const showShare = (field, params) => formatMoney(shownValue(field, params.id, params.value));
  const sortByShare = field => (v1, v2, cell1, cell2) => gridNumberComparator(shownValue(field, cell1.id, v1), shownValue(field, cell2.id, v2), cell1, cell2);
  const isShareCell = _ref4 => {
    var _splitShares$get2;
    let id = _ref4.id,
      field = _ref4.field;
    return ((_splitShares$get2 = splitShares.get(id)) === null || _splitShares$get2 === void 0 ? void 0 : _splitShares$get2[field]) !== undefined;
  };
  const finalColumns = [{
    field: 'unitName',
    headerName: 'Unit',
    width: 80,
    sortComparator: (v1, v2) => ('' + v1).localeCompare(v2, undefined, {
      numeric: true
    })
  }, {
    field: 'residentFullName',
    headerName: 'Resident',
    flex: 1,
    minWidth: 150
  }, {
    field: 'meterSerialNumber',
    headerName: 'Meter #',
    width: 100
  }, {
    field: 'startMeterDateOnly',
    headerName: 'Start date',
    width: 120,
    valueFormatter: params => params.value ? moment(params.value).format('MM/DD/YY') : ''
  }, {
    field: 'endMeterDateOnly',
    headerName: 'End date',
    width: 120,
    valueFormatter: params => params.value ? moment(params.value).format('MM/DD/YY') : ''
  }, {
    field: 'startMeterRead',
    headerName: 'Start read',
    width: 100,
    type: 'number',
    editable: true,
    align: 'left',
    headerAlign: 'left'
  }, {
    field: 'endMeterRead',
    headerName: 'End read',
    width: 100,
    type: 'number',
    editable: true,
    align: 'left',
    headerAlign: 'left'
  }, {
    field: 'meterUsage',
    headerName: 'Usage',
    width: 100,
    type: 'number',
    editable: true,
    align: 'left',
    headerAlign: 'left'
  }, {
    field: 'meterRate',
    headerName: 'Rate',
    width: 80,
    type: 'number',
    editable: true,
    align: 'left',
    headerAlign: 'left',
    cellClassName: params => params.row.isRateOverridden ? 'cell-rate-overridden' : '',
    valueFormatter: params => formatMeterRate(params.value)
  }, {
    field: 'unitsOfMeasure',
    headerName: 'UOM',
    width: 80
  }, {
    field: 'baseCharge',
    headerName: 'Base charge',
    width: 110,
    type: 'number',
    editable: true,
    align: 'left',
    headerAlign: 'left',
    valueFormatter: params => showShare('baseCharge', params),
    sortComparator: sortByShare('baseCharge')
  }, {
    field: 'usageCharge',
    headerName: 'Usage charge',
    width: 120,
    type: 'number',
    editable: true,
    align: 'left',
    headerAlign: 'left',
    valueFormatter: params => showShare('usageCharge', params),
    sortComparator: sortByShare('usageCharge')
  }, {
    field: 'allocationCharge',
    headerName: 'Total charge',
    width: 120,
    type: 'number',
    editable: true,
    align: 'left',
    headerAlign: 'left',
    valueFormatter: params => formatMoney(params.value)
  }, {
    field: 'serviceDaysInPeriod',
    headerName: 'Service days',
    width: 110,
    type: 'number',
    editable: true,
    align: 'left',
    headerAlign: 'left',
    // The backend stores whole days; a fraction or a negative stays in the cell, flagged, uncommitted.
    preProcessEditCellProps: params => {
      const value = params.props.value;
      const isWholeDays = value == null || value === '' || Number.isInteger(Number(value)) && Number(value) >= 0;
      return _objectSpread(_objectSpread({}, params.props), {}, {
        error: !isWholeDays
      });
    }
  }];
  const unmappedAlert = unmappedNotice ? /*#__PURE__*/React.createElement(Alert, {
    severity: "warning",
    sx: {
      mb: 2
    }
  }, unmappedNotice) : null;
  const staleAlert = isRecalculatedStale ? /*#__PURE__*/React.createElement(Alert, {
    severity: "warning",
    action: /*#__PURE__*/React.createElement(Button, {
      color: "inherit",
      size: "small",
      onClick: handleReloadRecalculatedRows,
      disabled: isRecalculating || isRefreshingRecalculatedRows
    }, "Reload rows")
  }, "The rows were recalculated, but they could not be refreshed. Reload them to continue.") : null;
  const parseWarningsBlock = /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(ParseWarnings, {
    warnings: parseWarnings,
    onFixMapping: canFixMapping ? handleFixMapping : undefined,
    isFixMappingLoading: isOpeningMapping
  }), isMappingOpen && /*#__PURE__*/React.createElement(OldUtilityConfigurationModal, {
    isOpen: true,
    handleClose: handleMappingClosed,
    utility: (currentPropertyDetails === null || currentPropertyDetails === void 0 ? void 0 : currentPropertyDetails.getUtilityTemplateModalForm) || null
  }));
  if (initialLoading) {
    return /*#__PURE__*/React.createElement("div", {
      className: "".concat(classes.wrapper, " ").concat(classes.submitterWrapper)
    }, /*#__PURE__*/React.createElement(BackdropLoader, {
      isOpen: true
    }));
  }

  // After another screen's row write, a failed read leaves it unknown whether the cycle has rows, and an
  // upload would replace any it has, so only Retry is offered until a read succeeds.
  if (rowsLoadFailed && !savedAllocations && (isCycleApproved || mustConfirmRows)) {
    return /*#__PURE__*/React.createElement("div", {
      className: "".concat(classes.wrapper, " ").concat(classes.submitterWrapper)
    }, /*#__PURE__*/React.createElement("div", {
      className: classes.header
    }, /*#__PURE__*/React.createElement(Typography, {
      variant: "h4"
    }, "Meter details")), /*#__PURE__*/React.createElement(Alert, {
      severity: "error",
      action: /*#__PURE__*/React.createElement(Button, {
        color: "inherit",
        size: "small",
        onClick: loadSavedRows
      }, "Retry")
    }, "The meter rows could not be loaded."), unmappedAlert);
  }
  if (isRecalculatedStale && !savedAllocations) {
    return /*#__PURE__*/React.createElement("div", {
      className: "".concat(classes.wrapper, " ").concat(classes.submitterWrapper)
    }, /*#__PURE__*/React.createElement("div", {
      className: classes.header
    }, /*#__PURE__*/React.createElement(Typography, {
      variant: "h4"
    }, "Meter details")), staleAlert, unmappedAlert);
  }
  if (savedAllocations) {
    return /*#__PURE__*/React.createElement("div", {
      className: "".concat(classes.wrapper, " ").concat(classes.submitterWrapper)
    }, /*#__PURE__*/React.createElement("div", {
      className: classes.header
    }, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(Typography, {
      variant: "h4"
    }, "Meter details"), /*#__PURE__*/React.createElement(Typography, {
      variant: "body2",
      className: classes.subtitle
    }, "Allocations saved successfully."))), isBillRemoved && /*#__PURE__*/React.createElement(Typography, {
      variant: "body2",
      role: "status"
    }, NO_BILL_NOTICE), parseWarningsBlock, unmappedAlert, /*#__PURE__*/React.createElement("div", {
      className: classes.tableWrapper
    }, /*#__PURE__*/React.createElement(DataGrid, {
      key: isBillRemoved ? 'read-only' : 'editable',
      autoHeight: true,
      columns: finalColumns,
      rows: finalRows,
      processRowUpdate: processFinalRowUpdate,
      isCellEditable: params => !isGridLocked && !isShareCell(params),
      onProcessRowUpdateError: () => toast('error', {
        body: ['That change could not be applied.']
      }),
      disableRowSelectionOnClick: true,
      hideFooter: true,
      columnHeaderHeight: 60,
      rowHeight: 40,
      initialState: {
        sorting: {
          sortModel: [{
            field: 'unitName',
            sort: 'asc'
          }]
        }
      },
      sx: {
        '& .MuiDataGrid-cell': {
          fontWeight: 400
        },
        '& .cell-rate-overridden': {
          backgroundColor: palette.info.light,
          color: palette.info.dark,
          borderLeft: "3px solid ".concat(palette.info.main)
        }
      }
    })), renderUsageWarning(sumMeteredUsage(savedAllocations, true)), renderMeterTotalWarnings(finalRows), staleAlert, !isStatementSent && !isBillRemoved && /*#__PURE__*/React.createElement(VarianceReview, {
      variance: variance,
      loadFailed: isVarianceFailed && !isVarianceLoading,
      isApproved: isCycleApproved,
      isBusy: isVarianceBusy,
      onReview: handleReview,
      onRetry: loadVariance
    }), !isStatementSent && !isBillRemoved && /*#__PURE__*/React.createElement("div", {
      className: classes.saveButtonWrapper
    }, !isCycleApproved && /*#__PURE__*/React.createElement(Button, {
      variant: "actionButtonInvert",
      onClick: () => {
        track(events.clicked('Recalculate Meter Allocations'), {
          page
        });
        setIsRecalculateConfirmOpen(true);
      },
      disabled: isGridLocked || isReviewing
    }, isRecalculating ? 'Recalculating...' : 'Recalculate'), /*#__PURE__*/React.createElement(Button, {
      variant: "contained",
      onClick: handleFinalSave,
      disabled: isGridLocked || isReviewing || store.allocations.putSubmeterBillingAllocationsAsync.inProgress
    }, isSaving ? 'Saving...' : 'Save'), /*#__PURE__*/React.createElement(Button, {
      variant: "contained",
      onClick: handleApproveAndSave,
      disabled: isVarianceBusy || !variance || variance.requiresAcknowledgement && !variance.isReviewed
    }, isApproveFlowRunning || isCheckingUnmapped ? 'Approving...' : 'Approve & save')), /*#__PURE__*/React.createElement(SimpleModal, {
      handleClose: () => setIsRecalculateConfirmOpen(false),
      isOpen: isRecalculateConfirmOpen,
      noPadding: true,
      rightButton: /*#__PURE__*/React.createElement(Button, {
        onClick: handleConfirmRecalculate,
        type: "button",
        variant: "contained"
      }, "Recalculate"),
      title: "Recalculate meter amounts?"
    }, /*#__PURE__*/React.createElement(Alert, {
      severity: "warning",
      icon: false
    }, /*#__PURE__*/React.createElement(Typography, {
      variant: "body2"
    }, "Every row is rebuilt from the saved meter reads, the current bill and the current meter mappings. Edits in this grid, including any rate you typed, are discarded. If a unit is no longer mapped on this utility, its charges are removed."))), /*#__PURE__*/React.createElement(SimpleModal, {
      handleClose: () => {
        unmappedContextRef.current = null;
        setIsUnmappedConfirmOpen(false);
      },
      isOpen: isUnmappedConfirmOpen,
      noPadding: true,
      rightButton: /*#__PURE__*/React.createElement(Button, {
        onClick: handleConfirmUnmapped,
        type: "button",
        variant: "contained"
      }, "Approve anyway"),
      title: "Approve with unmapped billable units?"
    }, /*#__PURE__*/React.createElement(Alert, {
      severity: "warning",
      icon: false
    }, /*#__PURE__*/React.createElement(Typography, {
      variant: "body2"
    }, unmappedSummary))), oldServicePeriod.confirmation);
  }
  return /*#__PURE__*/React.createElement("div", {
    className: "".concat(classes.wrapper, " ").concat(classes.submitterWrapper)
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.header
  }, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(Typography, {
    variant: "h4"
  }, "Meter details"), /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    className: classes.subtitle
  }, isBillRemoved ? NO_BILL_SUBTITLE : 'Enter meter reads manually or upload csv to auto-fill fields.')), !isBillRemoved && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Button, {
    variant: "actionButtonInvert",
    startIcon: /*#__PURE__*/React.createElement(Icon, {
      type: "file-text"
    }),
    onClick: () => {
      var _fileInputRef$current;
      track(events.clicked('Upload CSV Reads'), {
        page
      });
      (_fileInputRef$current = fileInputRef.current) === null || _fileInputRef$current === void 0 || _fileInputRef$current.click();
    },
    disabled: store.allocations.postSubmeterBillingParseCsvAsync.inProgress || isRecalculating
  }, store.allocations.postSubmeterBillingParseCsvAsync.inProgress ? 'Parsing...' : 'Upload csv reads'), /*#__PURE__*/React.createElement("input", {
    ref: fileInputRef,
    type: "file",
    accept: ".csv",
    hidden: true,
    onChange: handleCsvUpload
  }))), isBillRemoved && /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    role: "status"
  }, NO_BILL_NOTICE), parseWarningsBlock, unmappedAlert, parsedRows.length === 0 && /*#__PURE__*/React.createElement("div", {
    className: classes.emptyState
  }, /*#__PURE__*/React.createElement("img", {
    src: noDataSvg,
    alt: "No data",
    className: classes.emptyStateIcon
  }), /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    className: classes.emptyStateText
  }, isBillRemoved ? NO_BILL_EMPTY_STATE : 'Upload a file to continue')), parsedRows.length > 0 && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: classes.tableWrapper
  }, /*#__PURE__*/React.createElement(DataGrid, {
    key: isBillRemoved ? 'read-only' : 'editable',
    autoHeight: true,
    columns: columns,
    rows: rows,
    processRowUpdate: processRowUpdate,
    isCellEditable: () => !isBillRemoved,
    disableRowSelectionOnClick: true,
    hideFooter: true,
    columnHeaderHeight: 60,
    rowHeight: 40,
    initialState: {
      sorting: {
        sortModel: [{
          field: 'unitName',
          sort: 'asc'
        }]
      }
    },
    sx: {
      '& .MuiDataGrid-cell': {
        fontWeight: 400
      },
      '& .cell-readonly': {
        backgroundColor: '#f5f5f5',
        color: '#9e9e9e'
      },
      // An overridden rate is the manager's number rather than the calculated one, so it has
      // to read as edited. Deliberately unlike cell-readonly's grey, which says the opposite:
      // that the cell cannot be typed in at all. The left edge carries the same signal as the
      // fill, so the marker does not rely on colour alone. Weight is not available here:
      // styles.js pins .MuiDataGrid-cell and its descendants to font-weight 400 !important.
      '& .cell-rate-overridden': {
        backgroundColor: palette.info.light,
        color: palette.info.dark,
        borderLeft: "3px solid ".concat(palette.info.main)
      }
    }
  })), renderUsageWarning(sumMeteredUsage(parsedRows, false)), !isBillRemoved && /*#__PURE__*/React.createElement("div", {
    className: classes.saveButtonWrapper
  }, /*#__PURE__*/React.createElement(Button, {
    variant: "contained",
    onClick: handleSave,
    disabled: store.allocations.postSubmeterBillingAllocationsSaveAsync.inProgress
  }, store.allocations.postSubmeterBillingAllocationsSaveAsync.inProgress ? 'Saving...' : 'Add bill to meter'))));
});